import type { z } from 'zod';

import {
  getInstanceValueByAccountPath,
  getStructuralPath,
} from '@fragment/path-utils/path';
import type {
  BaseChartOfAccounts,
  BaseChartOfAccountsType,
} from '@fragment/types/schemas';

import { getAccountStatus } from '../status';

type ValidationResult = {
  message: string;
  path: (string | number)[];
};

type ValidatedAccount = ReturnType<
  typeof getStructuralPathToAccount<BaseChartOfAccountsType>
> extends Map<string, infer U>
  ? U
  : never;

/**
 * Gets a map of account paths to accounts from a validated chart of accounts
 */
export const getStructuralPathToAccount = <
  T extends z.infer<typeof BaseChartOfAccounts>
>(
  validatedCoA: T
): Map<string, T['accounts'][number]> => {
  const accountPathToAccount: Map<string, T['accounts'][number]> = new Map();
  const walk = (account: T['accounts'][number], path?: string) => {
    const newPath = path ? `${path}/${account.key}` : account.key;
    accountPathToAccount.set(newPath, account);
    account.children?.forEach((child) => walk(child, newPath));
  };
  validatedCoA.accounts.forEach((account) => walk(account));
  return accountPathToAccount;
};

/**
 * Validates that an account path exists in the chart of accounts
 */
export const validateAccountPathExists = (
  accountPath: string,
  accountPathToAccount: Map<string, ValidatedAccount>,
  context: string,
  path: (string | number)[]
): ValidationResult[] => {
  const structuralPath = getStructuralPath(accountPath);
  const account = accountPathToAccount.get(structuralPath);

  if (!account) {
    return [
      {
        message: `${context} refers to a non-existent account path: ${accountPath}`,
        path,
      },
    ];
  }

  return [];
};

/**
 * Validates that an account path doesn't contain problematic characters
 */
export const validateAccountPathFormat = (
  accountPath: string,
  context: string,
  path: (string | number)[]
): ValidationResult[] => {
  if (accountPath.includes('#')) {
    return [
      {
        message: `${context} account path '${accountPath}' cannot contain '#'`,
        path,
      },
    ];
  }

  return [];
};

/**
 * Validates that an account path is not empty
 */
export const validateAccountPathNotEmpty = (
  accountPath: string,
  context: string,
  path: (string | number)[]
): ValidationResult[] => {
  if (!accountPath || accountPath.trim() === '') {
    return [
      {
        message: `${context} must specify an account path`,
        path,
      },
    ];
  }

  return [];
};

/**
 * Comprehensive account path validation that combines all checks
 */
export const validateAccountPath = (
  accountPath: string,
  accountPathToAccount: Map<string, ValidatedAccount>,
  context: string,
  path: (string | number)[]
): ValidationResult[] => {
  const errors: ValidationResult[] = [];

  // Check if path is empty
  errors.push(...validateAccountPathNotEmpty(accountPath, context, path));

  // If path is empty, don't continue with other validations
  if (errors.length > 0) {
    return errors;
  }

  // Check format
  errors.push(...validateAccountPathFormat(accountPath, context, path));

  // Check if account exists
  errors.push(
    ...validateAccountPathExists(
      accountPath,
      accountPathToAccount,
      context,
      path
    )
  );

  return errors;
};

/**
 * Validates that an account is active (can be used in entries/flows)
 */
export const validateAccountIsActive = (
  accountPath: string,
  accountPathToAccount: Map<string, ValidatedAccount>,
  context: string,
  path: (string | number)[]
): ValidationResult[] => {
  const structuralPath = getStructuralPath(accountPath);
  const account = accountPathToAccount.get(structuralPath);

  if (!account) {
    // Account doesn't exist, but that's handled by validateAccountPathExists
    return [];
  }

  const accountStatus = getAccountStatus(account);
  if (accountStatus !== 'active') {
    return [
      {
        message: `${context} refers to an account with status '${account.status}'. Only active accounts can be used.`,
        path,
      },
    ];
  }

  return [];
};

/**
 * Validates template account parameters are properly filled
 */
export const validateTemplateAccountParameters = (
  accountPath: string,
  accountPathToAccount: Map<string, ValidatedAccount>,
  context: string,
  path: (string | number)[]
): ValidationResult[] => {
  const instanceValueMap = getInstanceValueByAccountPath(accountPath);
  const errors: ValidationResult[] = [];

  Array.from(instanceValueMap.keys()).forEach((ancestorPath) => {
    const structuralAncestorPath = getStructuralPath(ancestorPath);
    const ancestor = accountPathToAccount.get(structuralAncestorPath);

    if (!ancestor) {
      // This is handled by validateAccountPathExists
      return;
    }

    if (ancestor.template && !instanceValueMap.get(ancestorPath)) {
      errors.push({
        message: `Invalid account path for ${context.toLowerCase()}. This ${context.toLowerCase()} refers to a Template Account, but is missing a variable that identifies the instance of the Template Account. For example: ${ancestorPath}:{{var}}`,
        path,
      });
    }
  });

  return errors;
};
